import {
  Button,
  Checkbox,
  Flex,
  FormControl,
  HStack,
  Icon,
  IconButton,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Tag,
  Text,
  keyframes,
  useMediaQuery,
  useDisclosure,
} from '@chakra-ui/react';
import {
  EQUIPMENT_TYPES,
  canSelectEquipment,
  getEquipmentTypesForCrawler,
  TYPES_MAP,
  TYPE_ENABLED,
} from '#constants';
import { EditIcon, InfoIcon, WarningIcon } from '@chakra-ui/icons';
import { MdArrowDropDown, MdPeople } from 'react-icons/md';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { CHECKOUT_STAGES } from '#constants';
import DateWidget from './DateWidget.jsx';
import NewsBar from './NewsBar.jsx';
import { FaWheelchair } from 'react-icons/fa';
import Suggest from './Suggest.jsx';
import { debounce } from 'lodash-es';
import { shallow } from 'zustand/shallow';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUIStore } from '#store';
import { getTrackerDateErrors } from '#utils/trackerValidation';
import EmbassyInstructionsModal from '../Modals/EmbassyInstructionsModal.jsx';
import LiveAvailabilityModal from './LiveAvailabilityModal.jsx';
import usePrefetchLiveAvailability from './usePrefetchLiveAvailability.js';

export default function TrackWidget() {
  const trackerInput = useUIStore((state) => state.trackerInput, shallow);
  const {
    groupSize,
    equipmentType,
    startDate,
    endDate,
    filters: { campsites: selectedCampsiteIds, accessible },
    metadata,
  } = trackerInput;
  const {
    searchInput,
    setSearchInput,
    getSuggestedResults,
    setShowSuggestions,
    setEquipmentType,
    setGroupSize,
    toggleContactModal,
    user,
    sub,
    setShowPricingModal,
    trackErrors,
    setTrackErrors,
    outing,
    setAccessible,
    setCheckoutStage,
    setSteps,
    sendFBEvent,
    setBookingUrl,
    clearTripDraft,
    campsiteCatalog,
    putEvent,
  } = useUIStore(
    (state) => ({
      searchInput: state.searchInput,
      setSearchInput: state.setSearchInput,
      getSuggestedResults: state.getSuggestedResults,
      setShowSuggestions: state.setShowSuggestions,
      setEquipmentType: state.setEquipmentType,
      setGroupSize: state.setGroupSize,
      toggleContactModal: state.toggleContactModal,
      user: state.user,
      sub: state.sub,
      setShowPricingModal: state.setShowPricingModal,
      trackErrors: state.trackErrors,
      setTrackErrors: state.setTrackErrors,
      outing: state.outing,
      setAccessible: state.setAccessible,
      setCheckoutStage: state.setCheckoutStage,
      setSteps: state.setSteps,
      sendFBEvent: state.sendFBEvent,
      setBookingUrl: state.setBookingUrl,
      clearTripDraft: state.clearTripDraft,
      campsiteCatalog: state.campsites,
      putEvent: state.putEvent,
    }),
    shallow
  );
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryName = searchParams.get('name');
  const { isOpen: isInstructionsOpen, onOpen: onInstructionsOpen, onClose: onInstructionsClose } = useDisclosure();
  const {
    isOpen: isLiveAvailabilityOpen,
    onOpen: onLiveAvailabilityOpen,
    onClose: onLiveAvailabilityClose,
  } = useDisclosure();
  const [isMobile] = useMediaQuery('(max-width: 768px)');
  const canShowLiveAvailability =
    !!outing?.slug &&
    outing.entity_type !== 'recarea' &&
    outing.entity_type !== 'embassy';
  const liveAvailability = usePrefetchLiveAvailability({
    enabled: canShowLiveAvailability,
    slug: outing?.slug,
    startDate,
    groupSize,
    campsiteIds: selectedCampsiteIds?.length ? selectedCampsiteIds : undefined,
  });

  useEffect(() => {
    // Handle cases where the query params have data
    if (queryName) {
      setSearchInput(queryName);
      // if the entity is recarea, we need to search it
      if (outing?.entity_type === 'recarea') {
        getSuggestedResults(queryName);
      }
    }
  }, [queryName]);

  // BC Parks uses specific equipment labels; normalize generic Tent/RV to a valid option
  useEffect(() => {
    if (outing?.crawlerType !== 'bc-parks') return;
    const bcList = EQUIPMENT_TYPES['bc-parks'];
    if (Array.isArray(bcList) && !bcList.includes(equipmentType)) {
      setEquipmentType('1 Tent');
    }
  }, [outing?.crawlerType, equipmentType]);

  const getErrors = () => {
    if (!outing?._id) {
      return { searchInput: 'Please select a activity' };
    }
    if (outing.name.toLowerCase() !== searchInput.toLowerCase()) {
      return { searchInput: 'Please select a activity' };
    }
    const errors = getTrackerDateErrors({
      outing,
      startDate,
      endDate,
      equipmentType,
    });
    if (outing.entity_type === 'embassy') {
      const bookingUrl = metadata?.bookingUrl;
      if (!bookingUrl || bookingUrl.trim() === '') {
        errors.bookingUrl = 'Embassy appointment URL is required';
      } else {
        try {
          const urlObj = new URL(bookingUrl);
          if (!urlObj.protocol.startsWith('http')) {
            errors.bookingUrl = 'URL must start with http:// or https://';
          }
        } catch {
          errors.bookingUrl = 'Please enter a valid URL';
        }
      }
    }
    return errors;
  };

  const validate = () => {
    const errors = getErrors();
    setTrackErrors({ ...trackErrors, ...errors });
    if (Object.values(errors).find((e) => !!e)) {
      return false;
    } else {
      return true;
    }
  };

  const isUserVerified = () => {
    if (!user?.contacts?.[0]) return false;
    return user.contacts.some((c) => c.type === 'phone' && c.verified);
  };

  const handleClick = () => {
    const isValid = validate();
    if (!isValid) return;
    // TrackWidget only creates single-outing trackers; drop any leftover trip draft.
    clearTripDraft();
    setSteps(['pricingModal', 'checkout']);
    sendFBEvent({
      eventName: 'AddToCart',
      commerce: 'intent',
    });
    // Check is user has phone
    if (!isUserVerified()) {
      toggleContactModal(true);
    } else {
      if (sub?._id) {
        setShowPricingModal(false);
        navigate('/checkout');
        setCheckoutStage(CHECKOUT_STAGES[0]);
      } else {
        setShowPricingModal(true);
      }
    }
  };

  const canSelectGroup =
    TYPE_ENABLED.groupSize.indexOf(outing?.entity_type) > -1;
  const canSelectAccessible =
    TYPE_ENABLED.accessible.indexOf(outing?.entity_type) > -1;

  const debouncedGetSuggestedResults = useCallback(
    debounce(getSuggestedResults, 500),
    []
  );
  const error = Object.values(trackErrors).find((e) => !!e);
  const slideDown = keyframes`
    20%, 100% {
      transform: translate3d(0,90%,-10px);
    }
  `;

  const availableEquipments = getEquipmentTypesForCrawler(outing.crawlerType);

  return (
    <Flex
      align="stretch"
      direction="column"
      boxShadow="dark-lg"
      borderRadius="8px"
      p={2}
      w={['95%', '90%', '75%', 'unset']}
      alignSelf="center"
      backgroundColor="white"
      position="relative"
      sx={{ transformStyle: 'preserve-3d' }}
      zIndex={3}
    >
      <NewsBar />
      {error && ( // Mobile error
        <Flex
          display={['flex', 'none']}
          position={'absolute'}
          w="100%"
          transform="translateZ(-10px)"
          animation={`${slideDown} 2s linear 0s 1 forwards`}
          left={0}
          bottom={0}
          justify={'center'}
          align="center"
          transition="visibility 0.15s ease-out"
          visibility={error ? 'visible' : 'hidden'}
          mr={5}
          bg="white"
          py={[2, 1.5]}
          borderBottomRadius="lg"
          color="white"
          fontSize={['md', 'md']}
          fontWeight={500}
          textAlign={'center'}
          zIndex={2}
        >
          <WarningIcon mr={2} color="red.600" />
          <Text color="red.500">{error}</Text>
        </Flex>
      )}
      <Flex align={['center', 'stretch']} direction="column" w="100%">
        <Flex
          direction="row"
          justify={['center', 'space-between']}
          align="stretch"
          maxH="50%"
          w="100%"
        >
          <Flex ml={[0, 5]}>
            <Flex direction="row" m={2} justify="space-between" align="center">
              <Icon as={MdPeople} color="brand.500" boxSize={7} mr={1} />
              <NumberInput
                size="sm"
                maxW={16}
                defaultValue={1}
                min={1}
                onChange={setGroupSize}
                value={groupSize}
                max={50}
                isDisabled={!canSelectGroup}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </Flex>
            <Flex p={2} maxW="30vh" align={'center'}>
              <FormControl isInvalid={trackErrors.equipmentType}>
                <Select
                  icon={<MdArrowDropDown />}
                  placeholder="Select Type"
                  size="sm"
                  value={equipmentType}
                  onChange={(e) => {
                    setEquipmentType(e.target.value);
                  }}
                  disabled={!canSelectEquipment(outing)}
                  width={['100%', 'max-content']}
                >
                  {availableEquipments.map((entity) => (
                    <option key={entity} value={entity}>
                      {entity}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                isInvalid={trackErrors.accessible}
                width={['auto', '100%']}
              >
                <Checkbox
                  isChecked={accessible}
                  onChange={(e) => setAccessible(e.target.checked)}
                  disabled={!canSelectAccessible}
                  size="lg"
                  ml={4}
                  w="max-content"
                  colorScheme={'brand'}
                >
                  <HStack spacing={1} fontSize={'md'}>
                    <Icon as={FaWheelchair} color="brand.500"></Icon>
                    <Text display={['none', 'block']}>Accessible</Text>
                  </HStack>
                </Checkbox>
              </FormControl>
            </Flex>
          </Flex>
          <Flex
            justify={'center'}
            align="center"
            transition="visibility 0.15s ease-out"
            visibility={error ? 'visible' : 'hidden'}
            mr={5}
            display={['none', 'flex']}
          >
            <WarningIcon mr={2} color="red.600" />
            <Text color="red.500">{error}</Text>
          </Flex>
        </Flex>

        <Flex
          direction="row"
          justify="space-around"
          w={['100%', '100%', '100%', '90vw', '80vw']}
          flexFlow={['wrap', 'wrap', 'wrap', 'nowrap']}
        >
          <Flex
            direction="column"
            align="stretch"
            w={['100%', '100%', '100%', '50%']}
            mx={3}
            my={2}
          >
            <FormControl isInvalid={trackErrors.searchInput}>
              <Input
                placeholder="Search Campgrounds, Permits or Anything"
                size="md"
                flexShrink={2}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  debouncedGetSuggestedResults(e.target.value);
                }}
                onFocus={(e) => {
                  e.target.select();
                  setShowSuggestions(true);
                }}
                onBlur={() => {
                  setShowSuggestions(false);
                }}
                value={searchInput}
                textColor="blackAlpha.900"
                fontFamily="Roboto, Arial, sans-serif"
                autoComplete="off"
              />
            </FormControl>
            <Suggest />
          </Flex>
          <DateWidget />
          <Button
            colorScheme="brand"
            minW={'max-content'}
            size="md"
            mx={3}
            my={2}
            flexShrink={8}
            onClick={handleClick}
            textColor="white"
            fontFamily="Roboto, Arial, sans-serif"
          >
            Get Alerts Now
          </Button>
        </Flex>
        {outing?.entity_type === 'embassy' && (
          <Flex direction="row" mx={3} my={2} w={['100%', '100%', '100%', 'unset']}>
            <Flex align="center" mb={2} position="relative" mr={6}>
              <Text fontSize="md" fontWeight="semibold" w="max-content" display={['none', 'block']}>
                Appointment URL
              </Text>
              <Icon
                position="absolute"
                right={-4}
                top={[3, -1]}
                as={InfoIcon}
                size="sm"
                color="green.500"
                onClick={onInstructionsOpen}
                _hover={{
                  cursor: 'pointer',
                }}
                aria-label="Instructions"
              />
            </Flex>
            <FormControl isInvalid={trackErrors.bookingUrl} >
              <Input
                placeholder={isMobile ? 'Appointment URL' : 'https://www.ch-edoc-reservation.admin.ch/#/session?token=xxxxxx&locale=en-US'}
                value={metadata?.bookingUrl || ''}
                onChange={(e) => {
                  setBookingUrl(e.target.value);
                }}
                size="md"
                borderColor={trackErrors.bookingUrl ? 'red.300' : 'gray.300'}
                _focus={{
                  borderColor: trackErrors.bookingUrl ? 'red.500' : 'brand.500',
                  boxShadow: trackErrors.bookingUrl ? '0 0 0 1px #E53E3E' : '0 0 0 1px #3182CE',
                }}
                w="100%"
              />
            </FormControl>
          </Flex>
        )}
        <Flex
          direction="row"
          align="center"
          flexWrap="wrap"
          columnGap={1}
          rowGap={0}
          alignSelf="flex-start"
          w="100%"
        >
          <SelectedCampsites />
          {liveAvailability && (
            <Button
              size="sm"
              colorScheme="brand"
              variant="ghost"
              onClick={() => {
                putEvent({
                  type: 'live-availability-open',
                  data: {
                    outingName: outing?.name,
                    slug: outing?.slug,
                    groupSize,
                    startDate: liveAvailability?.initialStartDate,
                    selectedCampsiteCount: selectedCampsiteIds?.length || 0,
                  },
                });
                if (typeof window.gtag === 'function') {
                  window.gtag('event', 'live_availability_open', {
                    outing_name: outing?.name,
                    outing_slug: outing?.slug,
                  });
                }
                onLiveAvailabilityOpen();
              }}
              alignSelf="center"
              ml={1}
            >
              See Live Availability
            </Button>
          )}
        </Flex>
      </Flex>
      <ExtraInfo />
      <EmbassyInstructionsModal isOpen={isInstructionsOpen} onClose={onInstructionsClose} />
      <LiveAvailabilityModal
        isOpen={isLiveAvailabilityOpen}
        onClose={onLiveAvailabilityClose}
        slug={outing?.slug}
        displayName={outing?.name}
        groupSize={groupSize}
        campsiteIds={
          selectedCampsiteIds?.length ? selectedCampsiteIds : undefined
        }
        initialStartDate={liveAvailability?.initialStartDate}
        initialData={liveAvailability?.data}
        prefetchedWindows={liveAvailability?.windows}
      />
    </Flex>
  );
}

function ExtraInfo() {
  const { outing } = useUIStore(
    (state) => ({
      outing: state.outing,
    }),
    shallow
  );
  return (
    <Flex
      justify={'center'}
      align="center"
      transition="visibility 0.95s linear"
      mr={5}
      ml={[0, 0]}
      fontSize={'sm'}
      display={outing?.parent_name === 'Milford Track' ? 'flex' : 'none'}
      // visibility={
      //   outing?.parent_name === 'Milford Track' ? 'visible' : 'hidden'
      // }
      position={'absolute'}
      bottom={[-10, -5]}
      left={0}
      w="100%"
      bg="brand.200"
      py={0.5}
      pl={2}
      borderBottomRadius={'md'}
    >
      <InfoIcon mr={2} color="blue.600" />
      <Text color="blue.500">
        To track all huts for Milford Walk, choose entry date for Clinton Hut
      </Text>
    </Flex>
  );
}

function SelectedCampsites() {
  const { trackerInput, campsites, outing, toggleCampsitesModal } = useUIStore(
    (state) => ({
      trackerInput: state.trackerInput,
      campsites: state.campsites,
      outing: state.outing,
      toggleCampsitesModal: state.toggleCampsitesModal,
    }),
    shallow
  );
  const entityType = outing?.entity_type;
  const canSelectChild =
    TYPE_ENABLED.specific.indexOf(outing?.entity_type) > -1;
  const selectedCampsites = trackerInput?.filters?.campsites ?? [];
  const campsiteMap = useMemo(() => {
    return campsites?.reduce((prev, curr) => {
      prev[curr._id] = curr;
      return prev;
    }, {});
  }, [campsites]);
  const highlightAni = keyframes`
    0%, 100% {
      // box-shadow: none;
      background-color: transparent;
      color: green
    }
    40%, 60% {
      // box-shadow: 0 0 20px 1px green;
      background-color: #C6F6D5;
      // color: #68D391
    }
  `;
  const [animation, setAnimation] = useState('none');
  useEffect(() => {
    // Trying to highlight the button
    if (outing?._id) {
      setAnimation(`${highlightAni} 2s linear 0.7s infinite forwards`);
      setTimeout(() => setAnimation('none'), 4000);
    }
  }, [outing?._id]);
  if (!selectedCampsites?.length) {
    // Let's not show the button if we can't select child
    if (!canSelectChild) {
      return null;
    }
    return (
      <Button
        animation={campsites?.length > 0 ? animation : 'unset'}
        size="sm"
        colorScheme={'brand'}
        variant="ghost"
        onClick={() => toggleCampsitesModal(true)}
        isDisabled={!canSelectChild}
        alignSelf={'flex-start'}
        ml={1}
        opacity={campsites?.length > 0 ? 1 : 0.6}
      >
        {canSelectChild ? `Select Specific ${TYPES_MAP[entityType]}` : ''}
      </Button>
    );
  }
  return (
    <Flex
      my={1}
      ml={3}
      flexWrap={['wrap', 'nowrap']}
      rowGap={2}
      maxW={['100%', '70vw']}
      alignSelf={'flex-start'}
      alignItems="center"
      maxH={40}
      overflowY="scroll"
      overflowX="scroll"
    >
      <Text fontSize={'sm'} w="max-content" fontWeight={500} color="brand.700">
        {TYPES_MAP[entityType]}:
      </Text>
      <Flex display={['none', 'flex']} flexWrap="wrap">
        {selectedCampsites.map((c) => (
          <Tag key={c} colorScheme="green" mx={1} my={1}>
            <Text maxW={28} noOfLines={[1, 2]} py={'2px'}>
              {campsiteMap[c]?.name}
            </Text>
          </Tag>
        ))}
      </Flex>
      <Flex display={['flex', 'none']} ml={2}>
        <Tag colorScheme="green">{selectedCampsites.length} chosen </Tag>
      </Flex>
      <IconButton
        size="sm"
        h="24px"
        colorScheme={'brand'}
        variant="ghost"
        icon={<EditIcon boxSize={5} />}
        onClick={() => toggleCampsitesModal(true)}
      ></IconButton>
    </Flex>
  );
}
